import { useCallback, useEffect, useState } from 'react';
import { getActiveInitiativeId } from '../config';
import { apiFetch } from '../api/client';

/**
 * Reads the per-initiative feature flag for the hidden Validated Measure Hub
 * (Task #134): `data_capture_settings.sdoh_measure_hub_enabled`.
 *
 * Safe default OFF: with no active initiative, a failed fetch, or a missing
 * flag, this returns false so the hub stays hidden from nav. The backend
 * independently enforces the same flag + admin auth on every API call, so this
 * is a UI-visibility gate only, never the security boundary.
 */
export function useMeasureHubEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);

  const refresh = useCallback(async () => {
    const id = getActiveInitiativeId();
    if (!id) {
      setEnabled(false);
      return;
    }
    try {
      const res = await apiFetch(`/api/project-configs/${id}`, { context: 'admin' });
      if (!res.ok) {
        setEnabled(false);
        return;
      }
      const cfg = await res.json();
      const dc = cfg?.data_capture_settings || {};
      setEnabled(Boolean(dc.sdoh_measure_hub_enabled));
    } catch {
      setEnabled(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener('spartis:activeInitiativeChanged', onChange);
    window.addEventListener('spartis:measureHubFlagChanged', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('spartis:activeInitiativeChanged', onChange);
      window.removeEventListener('spartis:measureHubFlagChanged', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [refresh]);

  return enabled;
}
